import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ORDER } from "@/constants/testIds";

const TEMPLATES = [
  {
    label: "Vendor confirmed",
    subject: "Your order has been placed with the vendor",
    body:
      "Hello,\n\nA quick note to let you know your order has been placed with the vendor. We'll update you as soon as it arrives in the shop.\n\nWarm regards,\nThe Pro Shop",
  },
  {
    label: "Sizing check",
    subject: "A quick question on sizing",
    body:
      "Hello,\n\nBefore we place the order with the vendor, would you mind confirming the size you would like? Happy to discuss in person at the shop.\n\nWarm regards,\nThe Pro Shop",
  },
  {
    label: "Ready for pickup",
    subject: "Your order is ready for pickup",
    body:
      "Hello,\n\nYour order has arrived at the pro shop and is ready whenever you are. We will keep it behind the counter for you.\n\nWarm regards,\nThe Pro Shop",
  },
];

export default function EmailComposer({ order, onSent }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [logAsMessage, setLogAsMessage] = useState(true);
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null); // {type, text}

  const applyTemplate = (t) => {
    setSubject(t.subject);
    setBody(t.body);
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    setFeedback(null);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/email`, {
        subject: subject.trim(),
        body: body.trim(),
        log_as_message: logAsMessage,
      });
      onSent?.(data.order);
      setSubject("");
      setBody("");
      setFeedback({
        type: data.delivered ? "success" : "warning",
        text: data.delivered
          ? "Email delivered."
          : "Logged on the order. Email could not be delivered by Resend (verify your sending domain to reach this member's inbox).",
      });
      setTimeout(() => setFeedback(null), 8000);
    } catch (err) {
      setFeedback({
        type: "error",
        text: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="surface-card p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline">Personal Note</div>
          <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Email the Member</h2>
          <p className="text-sm text-[#7A7A7A] mt-1">
            Sends from the pro shop to{" "}
            <span className="font-mono text-[#1A422D]">{order.member_email}</span> — letterhead and signature applied automatically.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={ORDER.emailToggle}
          className="btn-secondary text-xs"
        >
          {open ? "Close" : "Compose"}
        </button>
      </div>

      {open && (
        <div className="mt-6 fade-up">
          {/* Templates */}
          <div className="flex flex-wrap gap-2 mb-5">
            {TEMPLATES.map((t) => (
              <button
                key={t.label}
                type="button"
                onClick={() => applyTemplate(t)}
                className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#B59A5A] text-[#8a6f33] bg-[#FBF6E8] hover:bg-[#F3ECD6] transition-colors"
              >
                {t.label}
              </button>
            ))}
          </div>

          <label className="label-elegant block mb-2">Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid={ORDER.emailSubject}
            className="input-field"
            placeholder="A quick note from the pro shop"
          />

          <label className="label-elegant block mt-5 mb-2">Message</label>
          <textarea
            rows={8}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid={ORDER.emailBody}
            className="input-field font-serif text-base leading-relaxed"
            placeholder={"Hello,\n\n…\n\nWarm regards,\nThe Pro Shop"}
          />

          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] cursor-pointer">
              <input
                type="checkbox"
                checked={logAsMessage}
                onChange={(e) => setLogAsMessage(e.target.checked)}
                className="accent-[#1A422D] w-4 h-4"
              />
              Also log this in the order&rsquo;s message thread
            </label>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !subject.trim() || !body.trim()}
              data-testid={ORDER.emailSend}
              className="btn-primary"
            >
              {sending ? "Sending…" : "Send Email"}
            </button>
          </div>

          {feedback && (
            <div
              className={`mt-5 px-4 py-3 text-sm border ${
                feedback.type === "success"
                  ? "border-[#1A422D] bg-[#E9F0EB] text-[#1A422D]"
                  : feedback.type === "warning"
                    ? "border-[#B59A5A] bg-[#FBF6E8] text-[#8a6f33]"
                    : "border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]"
              }`}
            >
              {feedback.text}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
